import { Component, ReactNode } from 'react';
import { captureException, setUser, addBreadcrumb } from '@/utils/sentry';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, MessageSquare } from 'lucide-react';
import { UserFeedback } from './UserFeedback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  userId?: string;
  userEmail?: string;
  context?: Record<string, unknown>; // Additional context for debugging
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  eventId?: string;
  showFeedback: boolean;
}

/**
 * Enhanced Error Boundary with Sentry integration
 * 
 * Features:
 * - Captures errors with full React context
 * - Sets user context for debugging
 * - Adds breadcrumbs before error
 * - Shows user feedback dialog
 * - Tracks error eventId for Sentry linking
 * 
 * Usage:
 * <ErrorBoundary userId={user?.id} context={{ feature: 'checkout' }}>
 *   <CheckoutForm />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, showFeedback: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Set user context if available
    if (this.props.userId) {
      setUser({ 
        id: this.props.userId,
        email: this.props.userEmail,
      });
    }

    // Add breadcrumb about component where error occurred
    addBreadcrumb(
      'React component error caught',
      'error_boundary',
      {
        componentStack: errorInfo.componentStack,
        ...this.props.context,
      }
    );

    // Report to Sentry with full context
    captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
      userId: this.props.userId,
      ...this.props.context,
    });

    this.setState({ 
      errorInfo,
      showFeedback: true,
    });

    // Call optional callback
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      showFeedback: false,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <>
          <div className="min-h-[300px] flex items-center justify-center p-6">
            <div className="text-center space-y-6 max-w-md">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Ups! Coś poszło nie tak
                </h2>
                <p className="text-sm text-gray-600">
                  Wystąpił nieoczekiwany błąd. Został on zgłoszony i pracujemy nad naprawą.
                </p>
              </div>

              {this.state.error && import.meta.env.DEV && (
                <div className="bg-gray-100 rounded-md p-3 text-left overflow-auto max-h-32">
                  <code className="text-xs text-red-600 font-mono">
                    {this.state.error.message}
                  </code>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Odśwież stronę
                </Button>
                
                <Button 
                  onClick={() => this.setState({ showFeedback: true })}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  Zgłoś problem
                </Button>
              </div>
            </div>
          </div>

          {/* Feedback Dialog */}
          <UserFeedback
            eventId={this.state.eventId}
            error={this.state.error}
            isOpen={this.state.showFeedback}
            onClose={() => this.setState({ showFeedback: false })}
          />
        </>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
